import * as Sentry from '@sentry/vue';
import { getOriginalErrorFromMCEvent } from '$utils/MC_Utils';

/**
 * Report a user-facing error toast to Sentry with request/response context.
 * Swallowed API failures otherwise never reach Sentry.
 */
export function captureUserFacingError(userMessage, error, context = {}) {
	if (!userMessage && !error) return;

	const api = extractApiErrorContext(error);

	Sentry.withScope((scope) => {
		scope.setLevel('error');
		scope.setTag('user_facing_error', 'true');

		if (api.method) scope.setTag('http.method', api.method);
		if (api.status) scope.setTag('http.status_code', String(api.status));

		scope.setContext('userFacingError', {
			message: userMessage,
			...context,
		});

		if (Object.keys(api).length) {
			scope.setContext('api', api);
		}

		const fingerprint = [
			'user-facing-error',
			userMessage || 'unknown',
			api.method || '',
			api.url || '',
			api.status ? String(api.status) : '',
		].filter(Boolean);
		scope.setFingerprint(fingerprint);

		if (error instanceof Error) {
			Sentry.captureException(error);
			return;
		}

		const label = userMessage || 'User-facing error';
		const detail = api.status
			? `${api.method || 'REQUEST'} ${api.url || ''} → ${api.status}`
			: api.message;

		Sentry.captureMessage(detail ? `${label} (${detail})` : label, 'error');
	});
}

export function notifyUserFacingError(vm, text, error, context = {}) {
	if (vm && typeof vm.$notify === 'function') {
		vm.$notify({
			group: 'mms-notifications',
			type: 'error',
			text,
		});
	}

	captureUserFacingError(text, error, context);
}

function extractApiErrorContext(error) {
	if (!error) return {};

	const original = typeof error === 'object' ? getOriginalErrorFromMCEvent(error) : null;
	const axiosError = findAxiosError(error) || findAxiosError(original);

	const response = axiosError?.response || (original?.status && original?.config ? original : null);
	const config = axiosError?.config || response?.config || {};
	const status = response?.status || axiosError?.status || original?.status;
	const data = response?.data ?? original?.data;
	const method = (config.method || '').toUpperCase();
	const url = config.url || '';

	const api = {};
	if (method) api.method = method;
	if (url) api.url = url;
	if (status) api.status = status;
	if (data !== undefined) api.responseData = sanitizeResponseData(data);
	if (axiosError?.message || original?.message) {
		api.message = axiosError?.message || original?.message;
	}
	if (config.baseURL) api.baseURL = config.baseURL;

	return api;
}

function findAxiosError(error) {
	if (!error || typeof error !== 'object') return null;
	if (error.isAxiosError || error.response || (error.config && error.request)) return error;
	if (error.error) return findAxiosError(error.error);
	if (error.originalEvent) return findAxiosError(error.originalEvent);
	return null;
}

function sanitizeResponseData(data) {
	try {
		const serialized = typeof data === 'string' ? data : JSON.stringify(data);
		// Keep Sentry payloads bounded
		return serialized.length > 4000 ? `${serialized.slice(0, 4000)}…` : data;
	} catch (e) {
		return '[unserializable response]';
	}
}
